import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useQuickscan } from '@/contexts/QuickscanContext';
import { ArrowRight, ArrowLeft, Leaf, SkipForward } from 'lucide-react';
import { Logo } from '../../Logo';

export const SustainabilitySubScreen = () => {
  const { data, updateExpansionDetails, nextStep, prevStep, skipSubScreen } = useQuickscan();
  
  const [sustainabilityData, setSustainabilityData] = useState({
    co2ReductionTarget: data.expansionDetails.sustainability?.co2ReductionTarget || 50,
    timeline: data.expansionDetails.sustainability?.timeline || 2030,
    certifications: data.expansionDetails.sustainability?.certifications || [] as string[],
    budget: data.expansionDetails.sustainability?.budget || 'medium',
    targets: data.expansionDetails.sustainability?.targets || [] as string[]
  });

  const handleNext = () => {
    updateExpansionDetails?.('sustainability', sustainabilityData);
    nextStep();
  };

  const handleSkip = () => {
    console.log('Skipping sustainability details');
    skipSubScreen?.();
  };

  const timelines = [2025, 2027, 2030, 2035, 2040, 2050];
  const certificationOptions = [
    'CO2 Neutraal',
    'BREEAM',
    'LEED',
    'ISO 14001',
    'Cradle to Cradle',
    'EU Taxonomie'
  ];

  const budgetOptions = [
    { value: 'low', label: 'Beperkt budget', description: '< €50.000' },
    { value: 'medium', label: 'Gemiddeld budget', description: '€50.000 - €250.000' },
    { value: 'high', label: 'Ruim budget', description: '> €250.000' }
  ];

  const toggleCertification = (cert: string) => {
    setSustainabilityData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Breadcrumb with logo */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-gray-600">
          <span>Expansieplannen</span> <span className="mx-2">{'>'}‍</span> <span className="text-energy-blue font-medium">Duurzaamheidsdoelen</span>
        </div>
        <Logo className="h-6 object-contain" />
      </div>

      <div className="text-center mb-8">
        <Leaf className="w-12 h-12 text-energy-green mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-energy-green mb-2">
          Duurzaamheidsdoelen
        </h2>
        <p className="text-gray-600">
          Welke ambities heb je op het gebied van duurzaamheid?
        </p>
      </div>

      <div className="space-y-6 mb-8">
        {/* CO2 Reduction Target */}
        <div>
          <label className="block text-sm font-medium mb-2">
            CO2 reductie doelstelling (%) <span className="text-red-500">*</span>
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={sustainabilityData.co2ReductionTarget}
            onChange={(e) => setSustainabilityData(prev => ({ ...prev, co2ReductionTarget: parseInt(e.target.value) }))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-xs text-gray-500 mt-1">
            Huidige waarde: {sustainabilityData.co2ReductionTarget}%
          </div>
        </div>

        {/* Timeline */}
        <div>
          <label className="block text-sm font-medium mb-3">
            Tijdslijn <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-3">
            {timelines.map((timeline) => (
              <button
                key={timeline}
                onClick={() => setSustainabilityData(prev => ({ ...prev, timeline }))}
                className={`p-3 rounded-lg border text-sm transition-all ${
                  sustainabilityData.timeline === timeline
                    ? 'border-energy-blue bg-energy-blue/10 text-energy-blue font-medium' 
                    : 'border-gray-200 hover:border-energy-blue/50'
                }`}
              >
                {timeline}
              </button>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div>
          <label className="block text-sm font-medium mb-3">
            Certificeringen
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {certificationOptions.map((cert) => (
              <button
                key={cert}
                onClick={() => toggleCertification(cert)}
                className={`p-3 rounded-lg border text-sm transition-all ${
                  sustainabilityData.certifications.includes(cert)
                    ? 'border-energy-blue bg-energy-blue/10 text-energy-blue font-medium' 
                    : 'border-gray-200 hover:border-energy-blue/50'
                }`}
              >
                {cert}
              </button>
            ))}
          </div>
        </div>

        {/* Budget */}
        <div>
          <label className="block text-sm font-medium mb-3">
            Budgetindicatie
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {budgetOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => setSustainabilityData(prev => ({ ...prev, budget: option.value }))}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  sustainabilityData.budget === option.value
                    ? 'border-energy-blue bg-energy-blue/5' 
                    : 'border-gray-200 hover:border-energy-blue/50'
                }`}
              >
                <h3 className="font-semibold mb-1">{option.label}</h3>
                <p className="text-sm text-gray-600">{option.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={prevStep}
          className="border-gray-300"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Terug
        </Button>

        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleSkip}
            className="border-gray-400 text-gray-600 hover:bg-gray-50"
          >
            <SkipForward className="w-4 h-4 mr-1" /> Overslaan
          </Button>
          
          <Button 
            onClick={handleNext}
            className="bg-[#014336] text-white hover:bg-[#014336]/90"
          >
            Volgende <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};
